import Swiper from '@app/domains/common/components/Swiper';
import useInViewForPosthog from '@app/domains/common/hooks/useInViewForPosthog';
import { HomeCollectionSlots } from '@app/domains/home/analytics/posthog/constants';
import { HomeEvents } from '@app/domains/home/analytics/posthog/events';
import HomeFeaturedCard from '@app/domains/home/components/HomeFeaturedCard';
import HomeFeaturedCardSkeleton from '@app/domains/home/components/HomeFeaturedCard/HomeFeaturedCardSkeleton';
import HomeSwiperController from '@app/domains/home/components/HomeSwiperController';
import useHomeMediaQuery from '@app/domains/home/hooks/useHomeMediaQuery';
import usePlacementEntries from '@app/domains/home/hooks/usePlacementEntries';
import {
    HOME_SWIPER_LAYOUT_STYLE,
    HOME_SWIPER_PAGINATION_ACTIVE_BULLET_STYLE,
    HOME_SWIPER_PAGINATION_BULLET_STYLE,
} from '@app/domains/home/styles';
import { PlacementId } from '@app/domains/home/types';
import { Suspense } from 'react';

function Placeholders() {
    const { isTablet } = useHomeMediaQuery();

    return (
        <div className="flex space-x-12 pb-38 md:pb-48">
            <HomeFeaturedCardSkeleton />
            {isTablet && <HomeFeaturedCardSkeleton />}
        </div>
    );
}

function HomeFeaturedCards() {
    const { isMobile, isTablet, isDesktop, isLargeScreen, isUpMd } = useHomeMediaQuery();

    const { originData: featuredCardsData } = usePlacementEntries({
        placementId: PlacementId.Featured,
        enabled: true,
        staleTime: 1000 * 60 * 60,
        suspense: true,
    });

    const { ref } = useInViewForPosthog({
        event: HomeEvents.ViewCollection,
        triggerOnce: true,
    });

    const featuredCards = featuredCardsData?.items || [];

    const slidesPerView = (() => {
        if (isTablet) return 2;
        return 1;
    })();

    const spaceBetween = (() => {
        if (isMobile) return 8;
        if (isTablet) return 12;
        if (isDesktop) return 16;
        if (isLargeScreen) return 20;
        return 20;
    })();

    return (
        <article ref={ref} data-posthog-params={JSON.stringify({ collectionSlot: HomeCollectionSlots.Featured })}>
            <Swiper
                style={!isLargeScreen ? { overflow: 'visible' } : {}}
                className={HOME_SWIPER_LAYOUT_STYLE}
                slidesPerView={slidesPerView}
                slidesPerGroup={slidesPerView}
                spaceBetween={spaceBetween}
                navigation={isUpMd}
                pagination={{
                    clickable: isUpMd,
                    bulletClass: HOME_SWIPER_PAGINATION_BULLET_STYLE,
                    bulletActiveClass: HOME_SWIPER_PAGINATION_ACTIVE_BULLET_STYLE,
                }}
            >
                {featuredCards.map((featuredCard, slideIndex) => (
                    <Swiper.Slide key={slideIndex} data-posthog-params={JSON.stringify({ itemSlot: slideIndex })}>
                        <HomeFeaturedCard content={HomeFeaturedCard.parsePlacementParams(featuredCard)} />
                    </Swiper.Slide>
                ))}
                {
                    <HomeSwiperController>
                        {isUpMd && <HomeSwiperController.PrevButton />}
                        <HomeSwiperController.Pages />
                        {isUpMd && <HomeSwiperController.NextButton />}
                    </HomeSwiperController>
                }
            </Swiper>
        </article>
    );
}

export default function HomeFeaturedCardsWrapper() {
    return (
        <Suspense fallback={<Placeholders />}>
            <HomeFeaturedCards />
        </Suspense>
    );
}
